import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {Application} from "../../model/application";
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ApplicationService } from 'src/app/services/application/application.service';
import { ColumnDef } from 'src/app/model/column-definition';
import { MatPaginator } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/constants/app-constants';
import { Subscription } from 'rxjs';

/**
 * ApplicationsPage represenents the 'applications' menu
 */
@Component({
  selector: 'app-applications',
  templateUrl: './applications-page.component.html',
  styleUrl: './applications-page.component.scss'
})
export class ApplicationsPageComponent implements AfterViewInit {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, {static: true}) sort: MatSort = new MatSort();

  /** The table of applications */
  @ViewChild(MatTable) table!: MatTable<Application>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<Application>([]);

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new suppliers */
  private _sub: Subscription | null = null;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    {header: "Application ID", field: "applicationGuid"},
    {header: "Name", field: "name"},
    {header: "Company Name", field: "businessName"},
    //{header: "Applied", field: "appliedOn", format: "date", toStringTransform: (x) => new Date(x).toDateString()},
    {header: "Spread", field: "borrowerApr", format: "percentage"},
    {header: "Phone Number", field: "phoneNumber", format: "phone"},
    {header: "Amount", field: "approvedAmount", format: "currency", toStringTransform: (x) => `$${x}`},
    {header: "Status", field: "status", format: "status"},
  ]
  public displayedCols: string[] = this.colDefs.map(x => x.field);

  /**
   * Constructor for the application page to initialize services and set a base snapshot 
   * 
   * @param _applicationService 
   */
  constructor(
    private _applicationService: ApplicationService,
    private _router: Router,
  ) {
    // Subscribe to timer changes
    this._applicationService.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });
  }

  /**
   * Initialize the sort, paginator, and application data source
   */
  ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimieters
    this.source.filterPredicate = (data: Application, filter: string): boolean => {
      return data.toString(this.colDefs).includes(filter);
    }

    // Get the applications from the service
    this._sub = this._applicationService.applications$.subscribe(
      (resp: Application[]) => {
        this._populateSource(resp);
      });

    this._populateSource(this._applicationService.getApplications());
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: Application, property: string): string | number => {
      return (item as any)[property];
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._sub?.unsubscribe();
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Populate the data source meticulously so it doesn't lag the application
   * Note: I don't know if async is required, I'm just paranoid
   */
  private async _populateSource(incoming: Application[]): Promise<void> {
    // Don't update the table when the user is not on the page => angular will be mad :(
    // TODO: can add this clause to updating the source data. Can then add notifications to some future service from changes found here
    if (this._router.url.replace("/", "") !== AppConstants.MENU_APPLICATIONS) return;

    let source = this.source.data;
    const current = this.source.data.map((x: Application) => x.copyOf());

    // For each incoming application, determine if it exists in the source. 
    const update = async () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If application IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the applications are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf();
            }

            // We've updated the application, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    await update();

    // Add new applications that we don't currently have
    const add = async () => {
      for (let newApplication of incoming) {
        source.push(newApplication);
      }
    }
    await add();

    // Remove old applications that have been removed
    const remove = async () => {
      for (let oldApplication of current) {
        source = source.filter(x => x.id !== oldApplication.id);
      }
    }
    await remove();

    // Update the table
    this.source.data = source;
  }
}